<template>
  <div>
    <section class="al-card settings__section">
      <h3 class="settings__section-title">Security</h3>
      <TwoFASettings />
    </section>

    <section class="al-card settings__section">
      <h3 class="settings__section-title">Connected Apps (MCP)</h3>

      <div class="settings__data-row settings__data-row--top">
        <div>
          <p class="settings__data-label">MCP Connection URL</p>
          <p class="settings__data-hint">
            Paste this URL in Claude Desktop or Claude Code. The first connection
            will open a browser to authorize access.
          </p>
        </div>
        <div style="display:flex;gap:.5rem;flex-shrink:0">
          <button v-if="mcpUrl" class="settings__btn settings__btn--secondary" @click="copyMcpUrl">
            {{ copied ? 'Copied!' : 'Copy' }}
          </button>
          <button class="settings__btn" :disabled="generatingUrl" @click="generateMcpUrl">
            {{ generatingUrl ? 'Generating…' : mcpUrl ? 'Regenerate' : 'Generate URL' }}
          </button>
        </div>
      </div>

      <div v-if="mcpUrl" class="settings__mcp-url">
        <code>{{ mcpUrl }}</code>
      </div>

      <p v-if="regenWarning" class="settings__message settings__message--error" style="margin-top:.75rem">
        URL regenerated — all existing MCP connections have been invalidated.
      </p>

      <template v-if="activeTokens.length">
        <div class="settings__divider" />
        <p class="settings__data-label" style="margin-bottom:.75rem">Active sessions</p>
        <div v-for="token in activeTokens" :key="token.id" class="settings__data-row">
          <div>
            <p class="settings__data-label">{{ token.client_name }}</p>
            <p class="settings__data-hint">{{ token.scopes }} · expires {{ fmtDate(token.refresh_expires_at) }}</p>
          </div>
          <button class="settings__btn settings__btn--secondary" @click="revokeToken(token.id)">Revoke</button>
        </div>
      </template>
    </section>

    <section class="al-card settings__section">
      <h3 class="settings__section-title">Data</h3>

      <div class="settings__data-row">
        <div>
          <p class="settings__data-label">Export backup</p>
          <p class="settings__data-hint">Download all your data as a JSON file.</p>
        </div>
        <button class="settings__btn" :disabled="exporting" @click="handleExport">
          {{ exporting ? 'Exporting…' : 'Export' }}
        </button>
      </div>

      <div class="settings__divider" />

      <div class="settings__data-row">
        <div>
          <p class="settings__data-label">Import backup</p>
          <p class="settings__data-hint">Restore from a previously exported JSON file. This will replace all current data.</p>
        </div>
        <label class="settings__btn settings__btn--secondary" :class="{ 'settings__btn--disabled': importing }">
          {{ importing ? 'Importing…' : 'Import' }}
          <input
            v-if="!importing"
            type="file"
            accept=".json,application/json"
            style="display:none"
            @change="handleImport"
          />
        </label>
      </div>

      <p v-if="message" :class="['settings__message', messageType === 'error' ? 'settings__message--error' : 'settings__message--success']">
        {{ message }}
      </p>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import TwoFASettings from '@/components/TwoFASettings.vue';
import { API_URL } from '@/api.js';

// ── Connected Apps (MCP) ──────────────────────────────────────────────────────
const mcpUrl = ref(null);
const activeTokens = ref([]);
const generatingUrl = ref(false);
const copied = ref(false);
const regenWarning = ref(false);

function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleDateString() : '—';
}

async function loadMcp() {
  const [urlRes, tokenRes] = await Promise.all([
    fetch(`${API_URL}/settings/mcp/url`, { credentials: 'include' }),
    fetch(`${API_URL}/settings/oauth/tokens`, { credentials: 'include' }),
  ]);
  if (urlRes.ok) mcpUrl.value = (await urlRes.json()).url;
  if (tokenRes.ok) activeTokens.value = await tokenRes.json();
}

async function generateMcpUrl() {
  const isRegen = !!mcpUrl.value;
  if (isRegen && !confirm('Regenerating the URL will disconnect all existing MCP clients. Continue?')) return;
  generatingUrl.value = true;
  regenWarning.value = false;
  try {
    const res = await fetch(`${API_URL}/settings/mcp/url`, { method: 'POST', credentials: 'include' });
    if (res.ok) {
      mcpUrl.value = (await res.json()).url;
      if (isRegen) regenWarning.value = true;
    }
  } finally {
    generatingUrl.value = false;
  }
}

async function copyMcpUrl() {
  await navigator.clipboard.writeText(mcpUrl.value);
  copied.value = true;
  setTimeout(() => { copied.value = false; }, 2000);
}

async function revokeToken(tokenId) {
  await fetch(`${API_URL}/settings/oauth/tokens/${tokenId}`, { method: 'DELETE', credentials: 'include' });
  await loadMcp();
}

onMounted(loadMcp);

const exporting = ref(false);
const importing = ref(false);
const message = ref('');
const messageType = ref('success');

async function handleExport() {
  exporting.value = true;
  message.value = '';
  try {
    const response = await fetch(`${API_URL}/backup/export`, { credentials: 'include' });
    if (response.status === 401) { window.location.href = '/login'; return; }
    if (!response.ok) throw new Error('Export failed');

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `pmm_backup_${timestamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    messageType.value = 'error';
    message.value = 'Export failed. Please try again.';
  } finally {
    exporting.value = false;
  }
}

async function handleImport(event) {
  const file = event.target.files?.[0];
  if (!file) return;

  if (!confirm('This will replace all current data with the backup. Continue?')) {
    event.target.value = '';
    return;
  }

  importing.value = true;
  message.value = '';
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/backup/import`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (response.status === 401) { window.location.href = '/login'; return; }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Import failed');
    }

    const result = await response.json();
    const counts = Object.entries(result.imported)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `${v} ${k}`)
      .join(', ');
    messageType.value = 'success';
    message.value = `Import successful: ${counts}.`;
  } catch (e) {
    messageType.value = 'error';
    message.value = e.message || 'Import failed. Please try again.';
  } finally {
    importing.value = false;
    event.target.value = '';
  }
}
</script>

<style scoped>
.settings__section {
  padding: 1.75rem 2rem;
  margin-bottom: 1.5rem;
}

.settings__section-title {
  font-family: var(--al-font-headline);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--al-on-surface);
  margin: 0 0 1.5rem;
  text-transform: uppercase;
}

.settings__data-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.settings__data-label {
  font-weight: 600;
  color: var(--al-on-surface);
  margin: 0 0 0.25rem;
}

.settings__data-hint {
  font-size: 0.85rem;
  color: var(--al-on-surface-muted, #888);
  margin: 0;
}

.settings__divider {
  border: none;
  border-top: 1px solid var(--al-outline, #e0e0e0);
  margin: 1.25rem 0;
}

.settings__btn {
  flex-shrink: 0;
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: var(--al-primary, #1a73e8);
  color: #fff;
  transition: opacity 0.15s;
}

.settings__btn:disabled,
.settings__btn--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.settings__btn--secondary {
  background: transparent;
  border: 1.5px solid var(--al-primary, #1a73e8);
  color: var(--al-primary, #1a73e8);
  display: inline-flex;
  align-items: center;
}

.settings__message {
  margin-top: 1rem;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
}

.settings__message--success {
  background: #e6f4ea;
  color: #1e7e34;
}

.settings__message--error {
  background: #fdecea;
  color: #c0392b;
}

.settings__data-row--top {
  align-items: flex-start;
}

.settings__new-client {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0.75rem 0;
}

.settings__input {
  padding: 0.5rem 0.75rem;
  border: 1.5px solid var(--al-outline, #ddd);
  border-radius: 6px;
  font-size: 0.875rem;
  background: var(--al-surface, #fff);
  color: var(--al-on-surface);
  outline: none;
}

.settings__input:focus {
  border-color: var(--al-primary, #1a73e8);
}

.settings__credentials {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin: 0.75rem 0;
  font-size: 0.85rem;
}

.settings__credentials-warning {
  color: #e65100;
  font-weight: 600;
  margin: 0 0 0.5rem;
}

.settings__cred-row {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
  align-items: baseline;
}

.settings__cred-row span {
  min-width: 100px;
  color: var(--al-on-surface-variant);
  font-size: 0.8rem;
}

.settings__cred-row code {
  font-family: var(--al-font-mono, monospace);
  font-size: 0.8rem;
  word-break: break-all;
  background: #f5f5f5;
  padding: 0.1rem 0.3rem;
  border-radius: 3px;
}

.settings__btn--danger {
  background: #fdecea;
  color: #c0392b;
  border: 1.5px solid #e74c3c;
}

.settings__mcp-url {
  margin-top: .75rem;
  padding: .625rem .875rem;
  background: var(--al-surface-variant, #f5f5f5);
  border-radius: 6px;
  overflow-x: auto;
}

.settings__mcp-url code {
  font-family: var(--al-font-mono, monospace);
  font-size: .8rem;
  color: var(--al-on-surface);
  white-space: nowrap;
}
</style>
